import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import MomLayout from './MomLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useMomAuth } from '../../context/MomAuthContext';
import { toast } from 'sonner';
import { Heart, Mail, Phone, User, Lock, ArrowRight, Loader2 } from 'lucide-react';

export default function MomRegisterPage() {
  const { register } = useMomAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '', password: '' });
  const [busy, setBusy] = useState(false);

  const canSubmit = form.first_name.trim() && form.last_name.trim() && form.email.trim() && form.phone.trim() && form.password.length >= 8;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) { toast.error('Lütfen tüm alanları doldurun (şifre en az 8 karakter)'); return; }
    setBusy(true);
    try {
      await register({
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
        password: form.password,
      });
      toast.success('Hoş geldin! Hadi başlayalım 🤍');
      navigate('/mom/onboarding', { replace: true });
    } catch (err) {
      toast.error(err.message || 'Kayıt başarısız');
    } finally {
      setBusy(false);
    }
  };

  return (
    <MomLayout>
      {/* Top decorative shape */}
      <div className="absolute top-0 left-0 right-0 h-56 overflow-hidden -z-10 pointer-events-none">
        <div className="absolute -top-20 -left-10 w-72 h-72 bg-[#FFD6D0] rounded-full opacity-70 blur-2xl"></div>
        <div className="absolute top-10 -right-20 w-60 h-60 bg-[#FEF3C7] rounded-full opacity-60 blur-2xl"></div>
      </div>

      <div className="flex items-center gap-2 mb-6" data-testid="mom-register-header">
        <div className="w-10 h-10 rounded-2xl bg-[#FB7185] flex items-center justify-center text-white">
          <Heart className="w-5 h-5" fill="currentColor" />
        </div>
        <span className="font-semibold text-[#2D2422] text-lg">BabyFace AI</span>
      </div>

      <h1 className="text-4xl font-bold text-[#2D2422] leading-tight mb-2">
        Merhaba anne,<br/>hoş geldin.
      </h1>
      <p className="text-base text-[#7A6C68] mb-8">
        Bebeğinin ilk resimlerini görmeye hazır mısın?
      </p>

      <form onSubmit={submit} className="space-y-4" data-testid="mom-register-form">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="first_name" className="text-[#2D2422] text-sm">Ad</Label>
            <div className="relative mt-1.5">
              <User className="w-4 h-4 text-[#7A6C68] absolute left-4 top-1/2 -translate-y-1/2" />
              <Input id="first_name" type="text" required value={form.first_name}
                onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                placeholder="Ayşe"
                className="pl-11 h-14 rounded-2xl bg-white border-[#F3E5E0] focus:border-[#FB7185] text-base"
                data-testid="mom-register-first-name" />
            </div>
          </div>
          <div>
            <Label htmlFor="last_name" className="text-[#2D2422] text-sm">Soyad</Label>
            <div className="relative mt-1.5">
              <Input id="last_name" type="text" required value={form.last_name}
                onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                placeholder="Yılmaz"
                className="pl-4 h-14 rounded-2xl bg-white border-[#F3E5E0] focus:border-[#FB7185] text-base"
                data-testid="mom-register-last-name" />
            </div>
          </div>
        </div>

        <div>
          <Label htmlFor="email" className="text-[#2D2422] text-sm">E-posta</Label>
          <div className="relative mt-1.5">
            <Mail className="w-4 h-4 text-[#7A6C68] absolute left-4 top-1/2 -translate-y-1/2" />
            <Input id="email" type="email" required value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="ornek@mail.com"
              className="pl-11 h-14 rounded-2xl bg-white border-[#F3E5E0] focus:border-[#FB7185] text-base"
              data-testid="mom-register-email" />
          </div>
        </div>

        <div>
          <Label htmlFor="phone" className="text-[#2D2422] text-sm">Telefon</Label>
          <div className="relative mt-1.5">
            <Phone className="w-4 h-4 text-[#7A6C68] absolute left-4 top-1/2 -translate-y-1/2" />
            <Input id="phone" type="tel" required value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="05XX XXX XX XX"
              className="pl-11 h-14 rounded-2xl bg-white border-[#F3E5E0] focus:border-[#FB7185] text-base"
              data-testid="mom-register-phone" />
          </div>
        </div>

        <div>
          <Label htmlFor="password" className="text-[#2D2422] text-sm">Şifre</Label>
          <div className="relative mt-1.5">
            <Lock className="w-4 h-4 text-[#7A6C68] absolute left-4 top-1/2 -translate-y-1/2" />
            <Input id="password" type="password" required value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder="En az 8 karakter"
              autoComplete="new-password"
              className="pl-11 h-14 rounded-2xl bg-white border-[#F3E5E0] focus:border-[#FB7185] text-base"
              data-testid="mom-register-password" />
          </div>
        </div>

        <Button type="submit" disabled={busy || !canSubmit}
          className="w-full h-14 rounded-2xl bg-[#FB7185] hover:bg-[#F43F5E] text-white text-base font-semibold mt-4 disabled:opacity-60"
          data-testid="mom-register-submit-btn">
          {busy ? (<><Loader2 className="w-5 h-5 mr-2 animate-spin" />Kaydediliyor...</>) : (<>Hesap Oluştur<ArrowRight className="w-5 h-5 ml-2" /></>)}
        </Button>
      </form>

      <p className="text-center text-sm text-[#7A6C68] mt-6">
        Zaten hesabın var mı?{' '}
        <Link to="/mom/login" className="font-semibold text-[#FB7185] hover:text-[#F43F5E]" data-testid="mom-register-login-link">
          Giriş yap
        </Link>
      </p>
    </MomLayout>
  );
}
